import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast, Toaster } from "sonner";

import Header from "../components/Header";
import { api, formatApiErrorDetail } from "../lib/api";

const STATUS_FILTERS = [
  { key: "active", labelKey: "roster.filter_active" },
  { key: "archived", labelKey: "roster.filter_archived" },
  { key: "all", labelKey: "roster.filter_all" },
];

function fmtDate(iso) {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
}

export default function Roster() {
  const { t } = useTranslation();
  const [filter, setFilter] = useState("active");
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter !== "all") params.status = filter;
      if (q) params.q = q;
      const { data } = await api.get("/consultants", { params });
      setItems(data);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || t("common.error_generic")
      );
    } finally {
      setLoading(false);
    }
  }, [filter, q, t]);

  useEffect(() => {
    load();
  }, [load]);

  const doAction = async (id, action) => {
    setBusyId(id);
    try {
      if (action === "delete") {
        await api.delete(`/consultants/${id}`);
        toast.success(t("roster.deleted_toast"));
      } else {
        await api.post(`/consultants/${id}/${action}`);
        toast.success(
          action === "archive" ? t("roster.archived_toast") : t("roster.unarchived_toast")
        );
      }
      await load();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || t("common.error_generic")
      );
    } finally {
      setBusyId(null);
    }
  };

  const rows = useMemo(() => items, [items]);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="roster-page">
      <Toaster richColors position="top-right" />
      <Header />
      <main className="mx-auto max-w-6xl px-6 py-10">
        <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">
              {t("roster.title")}
            </h1>
            <p className="mt-2 text-sm text-slate-600">{t("roster.subtitle")}</p>
          </div>
          <Link
            to="/roster/new"
            className="inline-flex items-center rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700"
            data-testid="roster-add-btn"
          >
            {t("roster.add_cta")}
          </Link>
        </div>

        <div className="mb-4 flex flex-wrap items-center gap-3">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("roster.search_placeholder")}
            className="w-full max-w-xs rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
            data-testid="roster-search-input"
          />
          <div className="flex flex-wrap gap-2" data-testid="roster-filters">
            {STATUS_FILTERS.map((f) => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                data-testid={`roster-filter-${f.key}`}
                className={
                  "rounded-full px-3 py-1.5 text-sm font-medium transition-colors " +
                  (filter === f.key
                    ? "bg-indigo-600 text-white shadow-sm"
                    : "bg-white text-slate-700 ring-1 ring-slate-200 hover:bg-slate-100")
                }
              >
                {t(f.labelKey)}
              </button>
            ))}
          </div>
        </div>

        {loading && (
          <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center text-slate-500 shadow-sm">
            {t("common.loading")}
          </div>
        )}

        {!loading && rows.length === 0 && (
          <div
            className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center shadow-sm"
            data-testid="roster-empty"
          >
            <h3 className="text-lg font-semibold text-slate-900">
              {t("roster.empty_title")}
            </h3>
            <p className="mt-2 text-sm text-slate-600">{t("roster.empty_body")}</p>
            <Link
              to="/roster/new"
              className="mt-6 inline-flex items-center rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700"
              data-testid="roster-empty-cta"
            >
              {t("roster.add_cta")}
            </Link>
          </div>
        )}

        {!loading && rows.length > 0 && (
          <div className="grid gap-4 md:grid-cols-2">
            {rows.map((c) => (
              <div
                key={c.id}
                className="flex flex-col rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
                data-testid={`consultant-card-${c.id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="text-base font-semibold text-slate-900">
                        {c.display_name}
                      </h3>
                      {c.redact_identity && (
                        <span
                          className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-slate-600 ring-1 ring-slate-200"
                          data-testid={`consultant-redacted-badge-${c.id}`}
                          title={t("roster.redact_tooltip")}
                        >
                          {t("roster.redacted")}
                        </span>
                      )}
                      {c.catalog_opt_in && (
                        <span
                          className="rounded-full bg-teal-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-teal-700 ring-1 ring-teal-100"
                          data-testid={`consultant-catalog-badge-${c.id}`}
                        >
                          {t("cvtheque.roster_badge")}
                        </span>
                      )}
                    </div>
                    <p className="mt-0.5 text-sm text-slate-600">{c.title}</p>
                  </div>
                  <StatusPill status={c.status} />
                </div>

                <dl className="mt-4 grid grid-cols-2 gap-y-1.5 text-xs">
                  <Item label={t("roster.seniority")} value={t(`roster.seniority_${c.seniority}`)} />
                  <Item label={t("roster.tjm")} value={`${c.tjm_target}/day`} />
                  <Item label={t("roster.availability")} value={fmtDate(c.availability_date)} />
                  <Item label={t("roster.remote")} value={t(`roster.remote_${c.remote_policy}`)} />
                </dl>

                {c.skills?.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1">
                    {c.skills.slice(0, 6).map((s) => (
                      <span
                        key={s}
                        className="rounded-md bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-700"
                      >
                        {s}
                      </span>
                    ))}
                    {c.skills.length > 6 && (
                      <span className="text-xs text-slate-500">+{c.skills.length - 6}</span>
                    )}
                  </div>
                )}

                <div className="mt-4 flex flex-wrap gap-2 border-t border-slate-100 pt-4">
                  <Link
                    to={`/roster/${c.id}`}
                    className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                    data-testid={`consultant-edit-btn-${c.id}`}
                  >
                    {t("common.edit")}
                  </Link>
                  {c.status === "active" ? (
                    <button
                      onClick={() => doAction(c.id, "archive")}
                      disabled={busyId === c.id}
                      className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                      data-testid={`consultant-archive-btn-${c.id}`}
                    >
                      {t("roster.archive")}
                    </button>
                  ) : (
                    <button
                      onClick={() => doAction(c.id, "unarchive")}
                      disabled={busyId === c.id}
                      className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                      data-testid={`consultant-unarchive-btn-${c.id}`}
                    >
                      {t("roster.unarchive")}
                    </button>
                  )}
                  <button
                    onClick={() => {
                      if (window.confirm(t("roster.confirm_delete", { name: c.display_name }))) {
                        doAction(c.id, "delete");
                      }
                    }}
                    disabled={busyId === c.id}
                    className="rounded-lg border border-rose-200 bg-white px-3 py-1.5 text-xs font-semibold text-rose-700 hover:bg-rose-50 disabled:opacity-50"
                    data-testid={`consultant-delete-btn-${c.id}`}
                  >
                    {t("common.delete")}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

function Item({ label, value }) {
  return (
    <>
      <dt className="font-medium text-slate-500">{label}</dt>
      <dd className="text-slate-800">{value}</dd>
    </>
  );
}

function StatusPill({ status }) {
  const { t } = useTranslation();
  const map = {
    active: "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-100",
    archived: "bg-slate-100 text-slate-600 ring-1 ring-slate-200",
  };
  return (
    <span
      className={
        "inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider " +
        (map[status] || map.archived)
      }
      data-testid={`consultant-status-${status}`}
    >
      {t(`roster.status_${status}`)}
    </span>
  );
}
